import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, Server, UserCheck, Cookie, Eye, FileText } from 'lucide-react';

const LOGO_URL = '/AIC-Logo.png';

const sections = [
  {
    icon: FileText,
    title: 'Information We Collect',
    content: 'We collect information you provide when registering, including your name, email address, and child profile details (name, age, gender). We also store task submissions, points, achievements, and leaderboard data generated through app usage.'
  },
  {
    icon: Server,
    title: 'How We Use Your Information',
    content: 'Your information is used to operate the AIC Super Muslims Challenge platform: track child progress, award points and achievements, generate leaderboard rankings, send notifications about submissions and approvals, and provide administrative oversight.'
  },
  {
    icon: UserCheck,
    title: 'Google Sign-In & Authentication',
    content: 'We offer Google Sign-In via AWS Cognito. When you use Google Sign-In, we receive your name, email address, and profile picture from Google. This data is used solely for authentication and is not shared with third parties.'
  },
  {
    icon: Cookie,
    title: 'Cookies & Session Tokens',
    content: 'We use HTTP-only cookies and JSON Web Tokens (JWT) for authentication. These are strictly necessary for the app to function. We do not use tracking cookies or third-party analytics.'
  },
  {
    icon: Eye,
    title: 'Data Sharing & Third Parties',
    content: 'We do not sell, trade, or share your personal data with third parties. Data is stored securely in MongoDB Atlas (AWS-hosted). Authentication is handled via AWS Cognito. No advertising or marketing platforms have access to your data.'
  },
  {
    icon: Shield,
    title: 'Data Security',
    content: 'We implement industry-standard security measures: passwords are bcrypt-hashed, JWTs are signed with a secret key, all API traffic is encrypted via HTTPS/TLS, and database connections use TLS with certificate validation.'
  },
  {
    icon: Mail,
    title: 'Contact Us',
    content: 'For questions about this privacy policy or to request deletion of your data, please contact Alnoor Islamic Center at admin@alnooric.org.'
  }
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#FFFBEB]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[#08513F] hover:text-[#064031] mb-8 transition-colors">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Home
        </Link>

        <div className="flex items-center gap-4 mb-8">
          <img 
            src={LOGO_URL} 
            alt="Alnoor Islamic Center" 
            className="h-14 w-14 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div>
            <h1 className="text-3xl font-bold text-[#264653] font-outfit">Privacy Policy</h1>
            <p className="text-[#6B7280] mt-1">Last updated: May 2026</p>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-[#D4AF37]/20 shadow-lg p-8 space-y-8">
          <p className="text-[#264653] leading-relaxed">
            Alnoor Islamic Center ("we," "our," or "us") operates the AIC Super Muslims Challenge platform. This privacy policy explains how we collect, use, and protect your personal information.
          </p>

          {sections.map((section, index) => (
            <div key={index} className="animate-slide-up" style={{ animationDelay: `${index * 0.05}s` }}>
              <div className="flex items-start gap-4">
                <div className="bg-[#FFFBEB] p-3 rounded-2xl shrink-0">
                  <section.icon className="w-6 h-6 text-[#08513F]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#264653] font-outfit mb-2">{section.title}</h2>
                  <p className="text-[#6B7280] leading-relaxed">{section.content}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <footer className="mt-12 text-center">
          <p className="text-sm text-[#6B7280]">
            © {new Date().getFullYear()} Alnoor Islamic Center. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
}
